import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { isTeacher } from '../../../utils/firebase';
import { collection, query, where, getDocs, onSnapshot, orderBy, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db, COLLECTIONS } from '../../../utils/firebase';
import LeftArrowIcon from '../../../components/icons/LeftArrowIcon';
import SearchIcon from '../../../components/icons/SearchIcon';
import DownIcon from '../../../components/icons/DownIcon';
import EditIcon from '../../../components/icons/EditIcon';
import SettingIcon from '../../../components/icons/SettingIcon';
import TrashIcon from '../../../components/icons/TrashIcon';
import LeftArrowDoubleIcon from '../../../components/icons/LeftArrowDoubleIcon';
import RightArrowDoubleIcon from '../../../components/icons/RightArrowDoubleIcon';
import RightArrowIcon from '../../../components/icons/RightArrowIcon';

interface SubjectStudent {
  id: string;
  name: string;
  email: string;
  phone?: string;
  grade: number;
  class: number;
  number: number;
  stampCount: number;
  subjectPoints: number;
  lastActivity: Date;
  isActive: boolean;
  avatar?: string;
  createdAt?: Date;
}

interface PendingStudent {
  id: string;
  uid: string;
  name: string;
  email: string;
  phone?: string;
  grade: number;
  class: number;
  number: number;
  schoolId: string;
  subjectId: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: Date;
}

const SubjectClassStudent: React.FC = () => {
  const { grade: gradeParam, class: classParam } = useParams<{ grade: string; class: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [students, setStudents] = useState<SubjectStudent[]>([]);
  const [pendingStudents, setPendingStudents] = useState<PendingStudent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState<'list' | 'pending'>('list');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPageOpen, setItemsPerPageOpen] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'stamp'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const grade = gradeParam ? parseInt(gradeParam) : 1;
  const classNumber = classParam ? parseInt(classParam) : 1;

  // 학생 데이터 로드
  useEffect(() => {
    if (!currentUser || !isTeacher(currentUser)) return;

    const loadStudents = async () => {
      try {
        setLoading(true);
        const studentsRef = collection(db, `schools/${currentUser.schoolId}/subjects/${currentUser.subjectId}/students`);
        const q = query(
          studentsRef,
          where('grade', '==', grade),
          where('class', '==', classNumber),
          orderBy('name')
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const studentsData = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              name: data.name || '',
              email: data.email || '',
              phone: data.phone || '',
              grade: data.grade || grade,
              class: data.class || classNumber,
              number: data.number || 1,
              stampCount: data.stampCount?.count || data.stampCount || 0,
              subjectPoints: data.subjectPoints || 0,
              lastActivity: data.lastActivity?.toDate() || new Date(),
              isActive: data.isActive !== false,
              avatar: data.avatar,
              createdAt: data.createdAt?.toDate() || new Date()
            } as SubjectStudent;
          });
          setStudents(studentsData);
          setLoading(false);
        });

        return () => unsubscribe();
      } catch (error) {
        console.error('학생 데이터 로드 오류:', error);
        setLoading(false);
      }
    };

    loadStudents();
  }, [currentUser, grade, classNumber]);

  // 승인 대기 학생 데이터 로드
  useEffect(() => {
    if (!currentUser || !isTeacher(currentUser) || selectedTab !== 'pending') return;

    const loadPendingStudents = async () => {
      try {
        const pendingRef = collection(db, 'subjectStudentRequests');
        const q = query(
          pendingRef,
          where('schoolId', '==', currentUser.schoolId),
          where('subjectId', '==', currentUser.subjectId),
          where('grade', '==', grade),
          where('class', '==', classNumber),
          where('status', '==', 'pending'),
          orderBy('createdAt', 'desc')
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const pendingData = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              uid: data.uid || '',
              name: data.name || '',
              email: data.email || '',
              phone: data.phone || '',
              grade: data.grade || grade,
              class: data.class || classNumber,
              number: data.number || 1,
              schoolId: data.schoolId || '',
              subjectId: data.subjectId || '',
              status: data.status || 'pending',
              createdAt: data.createdAt?.toDate() || new Date()
            } as PendingStudent;
          });
          setPendingStudents(pendingData);
        });

        return () => unsubscribe();
      } catch (error) {
        console.error('승인 대기 학생 데이터 로드 오류:', error);
      }
    };

    loadPendingStudents();
  }, [currentUser, grade, classNumber, selectedTab]);

  // 필터링 및 정렬된 학생 목록
  const filteredAndSortedStudents = useMemo(() => {
    if (selectedTab === 'list') {
      let filtered: SubjectStudent[] = [...students];

      // 검색 필터
      if (searchTerm) {
        filtered = filtered.filter((student: SubjectStudent) =>
          student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.email.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // 정렬
      filtered = filtered.sort((a, b) => {
        if (sortBy === 'name') {
          return sortOrder === 'asc'
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name);
        } else if (sortBy === 'stamp') {
          return sortOrder === 'asc'
            ? a.stampCount - b.stampCount
            : b.stampCount - a.stampCount;
        }
        return 0;
      });

      return filtered;
    } else {
      let filtered: PendingStudent[] = [...pendingStudents];

      // 검색 필터
      if (searchTerm) {
        filtered = filtered.filter((student: PendingStudent) =>
          student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.email.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      return filtered;
    }
  }, [students, pendingStudents, searchTerm, selectedTab, sortBy, sortOrder]);

  // 페이지네이션
  const totalPages = Math.ceil(filteredAndSortedStudents.length / itemsPerPage);
  const paginatedStudents = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedStudents.slice(startIndex, startIndex + itemsPerPage) as (SubjectStudent | PendingStudent)[];
  }, [filteredAndSortedStudents, currentPage, itemsPerPage]);

  const handleSort = (field: 'name' | 'stamp') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleDeleteStudent = async (studentId: string) => {
    if (!currentUser || !isTeacher(currentUser)) return;
    if (!window.confirm('정말 이 학생을 삭제하시겠습니까?')) return;

    try {
      await deleteDoc(doc(db, `schools/${currentUser.schoolId}/subjects/${currentUser.subjectId}/students`, studentId));
    } catch (error) {
      console.error('학생 삭제 오류:', error);
      alert('학생 삭제 중 오류가 발생했습니다.');
    }
  };

  if (loading) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div>로딩 중...</div>
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      height: '100%',
      position: 'relative',
      background: 'var(--Color-background-main, #F4F6FA)',
      overflow: 'hidden'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '1280px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '42px'
      }}>
        {/* 상단 헤더 */}
        <div style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '12px'
        }}>
          <button
            onClick={() => navigate('/subject/teacher/ClassManagement')}
            style={{
              width: '38px',
              height: '38px',
              padding: '4px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <LeftArrowIcon width={20} height={20} color="#0D0D0D" />
          </button>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              color: 'var(--Color-text-normal, #161616)',
              fontSize: '24px',
              fontFamily: 'Pretendard',
              fontWeight: '600',
              lineHeight: '36px'
            }}>
              {grade}학년 {classNumber}반
            </div>
            <div style={{
              color: 'var(--Color-text-assistive, #989898)',
              fontSize: '16px',
              fontFamily: 'Pretendard',
              fontWeight: '500',
              lineHeight: '24px'
            }}>
              {students.length}명
            </div>
          </div>
        </div>

        {/* 탭 및 검색 영역 */}
        <div style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px'
        }}>
          {/* 탭 */}
          <div style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '16px'
          }}>
            <button
              onClick={() => setSelectedTab('list')}
              style={{
                minHeight: '40px',
                padding: '8px 12px',
                background: selectedTab === 'list' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                color: selectedTab === 'list' ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-normal, #161616)',
                fontSize: '14px',
                fontFamily: 'Pretendard',
                fontWeight: '500',
                lineHeight: '21px'
              }}
            >
              학생 리스트
            </button>
            <button
              onClick={() => setSelectedTab('pending')}
              style={{
                minHeight: '40px',
                padding: '8px 12px',
                background: selectedTab === 'pending' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                color: selectedTab === 'pending' ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-normal, #161616)',
                fontSize: '14px',
                fontFamily: 'Pretendard',
                fontWeight: '500',
                lineHeight: '21px'
              }}
            >
              승인 대기 목록
            </button>
          </div>

          {/* 검색 및 페이지 크기 선택 */}
          <div style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '12px'
          }}>
            {/* 검색 필드 */}
            <div style={{
              flex: 1,
              height: '40px',
              position: 'relative'
            }}>
              <div style={{
                width: '100%',
                height: '40px',
                paddingLeft: '16px',
                paddingRight: '16px',
                paddingTop: '8px',
                paddingBottom: '8px',
                background: 'var(--Color-fill-white, white)',
                borderRadius: '999px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <SearchIcon width={20} height={20} color="#222222" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="검색어를 입력해 주세요.(이름 또는 이메일)"
                  style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    color: 'var(--Color-text-assistive, #989898)',
                    fontSize: '13px',
                    fontFamily: 'Pretendard',
                    fontWeight: '400',
                    lineHeight: '19.5px'
                  }}
                />
              </div>
            </div>

            {/* 페이지 크기 선택 */}
            <div style={{
              position: 'relative',
              height: '40px'
            }}>
              <button
                onClick={() => setItemsPerPageOpen(!itemsPerPageOpen)}
                style={{
                  width: '112px',
                  height: '40px',
                  padding: '12px',
                  background: 'var(--Color-fill-white, white)',
                  borderRadius: '8px',
                  outline: '1.20px var(--Color-line-normal, #F0F0F0) solid',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span style={{
                  flex: 1,
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: '13px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500',
                  lineHeight: '19.5px',
                  textAlign: 'left'
                }}>
                  {itemsPerPage}개씩 보기
                </span>
                <DownIcon width={24} height={24} fill="#161616" />
              </button>
              {itemsPerPageOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: '4px',
                  width: '112px',
                  background: 'white',
                  border: '1px solid var(--Color-line-normal, #F0F0F0)',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  zIndex: 1000
                }}>
                  {[10, 20, 30, 50].map((num) => (
                    <button
                      key={num}
                      onClick={() => {
                        setItemsPerPage(num);
                        setItemsPerPageOpen(false);
                        setCurrentPage(1);
                      }}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: 'none',
                        background: 'transparent',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        color: 'var(--Color-text-normal, #161616)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--Color-background-sub, #F8F8F8)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      {num}개씩 보기
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 학생 목록 테이블 */}
          <div style={{
            width: '100%',
            height: '514px',
            padding: '16px 20px',
            background: 'white',
            borderRadius: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            overflowY: 'auto'
          }}>
            {/* 테이블 헤더 */}
            <div style={{
              width: '100%',
              borderBottom: '1px var(--Color-line-normal, #F0F0F0) solid',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center'
            }}>
              <div style={{
                width: '123px',
                paddingBottom: '12px',
                paddingLeft: '16px',
                paddingRight: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '2px'
              }}>
                <div style={{
                  color: 'var(--Color-text-assistive, #989898)',
                  fontSize: '13px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500',
                  lineHeight: '19.5px'
                }}>
                  No
                </div>
              </div>
              <div style={{
                width: '131px',
                paddingBottom: '12px',
                paddingLeft: '8px',
                paddingRight: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '2px'
              }}>
                <div style={{
                  color: 'var(--Color-text-assistive, #989898)',
                  fontSize: '13px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500',
                  lineHeight: '19.5px'
                }}>
                  학번
                </div>
              </div>
              <div style={{
                width: '170px',
                paddingBottom: '12px',
                paddingLeft: '8px',
                paddingRight: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '2px'
              }}>
                <div style={{
                  color: 'var(--Color-text-assistive, #989898)',
                  fontSize: '13px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500',
                  lineHeight: '19.5px'
                }}>
                  이름
                </div>
                <button
                  onClick={() => handleSort('name')}
                  style={{
                    width: '20px',
                    height: '20px',
                    padding: '2px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <DownIcon width={16} height={16} fill="#989898" />
                </button>
              </div>
              <div style={{
                width: '290px',
                paddingBottom: '12px',
                paddingLeft: '8px',
                paddingRight: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  color: 'var(--Color-text-assistive, #989898)',
                  fontSize: '13px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500',
                  lineHeight: '19.5px'
                }}>
                  이메일
                </div>
              </div>
              <div style={{
                width: '230px',
                paddingBottom: '12px',
                paddingLeft: '8px',
                paddingRight: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  color: 'var(--Color-text-assistive, #989898)',
                  fontSize: '13px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500',
                  lineHeight: '19.5px'
                }}>
                  전화
                </div>
              </div>
              <div style={{
                width: '231px',
                paddingBottom: '12px',
                paddingLeft: '8px',
                paddingRight: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '2px'
              }}>
                <div style={{
                  color: 'var(--Color-text-assistive, #989898)',
                  fontSize: '13px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500',
                  lineHeight: '19.5px'
                }}>
                  도장
                </div>
                <button
                  onClick={() => handleSort('stamp')}
                  style={{
                    width: '20px',
                    height: '20px',
                    padding: '2px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <DownIcon width={16} height={16} fill="#989898" />
                </button>
              </div>
              <div style={{
                width: '65px',
                paddingBottom: '12px',
                paddingLeft: '8px',
                paddingRight: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  flex: 1,
                  color: 'var(--Color-text-assistive, #989898)',
                  fontSize: '13px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500',
                  lineHeight: '19.5px'
                }}>
                  관리
                </div>
              </div>
            </div>

            {/* 테이블 행 */}
            {paginatedStudents.length === 0 ? (
              <div style={{
                width: '100%',
                padding: '40px',
                textAlign: 'center',
                color: 'var(--Color-text-assistive, #989898)',
                fontSize: '14px',
                fontFamily: 'Pretendard'
              }}>
                {selectedTab === 'list' ? '등록된 학생이 없습니다.' : '승인 대기 중인 학생이 없습니다.'}
              </div>
            ) : (
              paginatedStudents.map((student, index) => (
                <div
                  key={student.id}
                  style={{
                    width: '100%',
                    borderBottom: '1px var(--Color-line-normal, #F0F0F0) solid',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center'
                  }}
                >
                  <div style={{
                    width: '123px',
                    padding: '12px 18px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    gap: '8px'
                  }}>
                    <div style={{
                      color: 'var(--Color-text-normal, #161616)',
                      fontSize: '14px',
                      fontFamily: 'Pretendard',
                      fontWeight: '400',
                      lineHeight: '21px'
                    }}>
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </div>
                  </div>
                  <div style={{
                    width: '131px',
                    padding: '12px 8px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    gap: '8px'
                  }}>
                    <div style={{
                      color: 'var(--Color-text-normal, #161616)',
                      fontSize: '14px',
                      fontFamily: 'Pretendard',
                      fontWeight: '400',
                      lineHeight: '21px'
                    }}>
                      {student.number}번
                    </div>
                  </div>
                  <div style={{
                    width: '170px',
                    padding: '12px 8px',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      color: 'var(--Color-text-normal, #161616)',
                      fontSize: '14px',
                      fontFamily: 'Pretendard',
                      fontWeight: '400',
                      lineHeight: '21px'
                    }}>
                      {student.name}
                    </div>
                  </div>
                  <div style={{
                    width: '290px',
                    padding: '12px 8px',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      color: 'var(--Color-text-normal, #161616)',
                      fontSize: '14px',
                      fontFamily: 'Pretendard',
                      fontWeight: '400',
                      lineHeight: '21px'
                    }}>
                      {student.email}
                    </div>
                  </div>
                  <div style={{
                    width: '230px',
                    padding: '12px 8px',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      color: 'var(--Color-text-normal, #161616)',
                      fontSize: '14px',
                      fontFamily: 'Pretendard',
                      fontWeight: '400',
                      lineHeight: '21px'
                    }}>
                      {student.phone || '-'}
                    </div>
                  </div>
                  <div style={{
                    width: '231px',
                    padding: '12px 8px',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: '8px'
                  }}>
                    <div style={{
                      color: 'var(--Color-text-normal, #161616)',
                      fontSize: '14px',
                      fontFamily: 'Pretendard',
                      fontWeight: '400',
                      lineHeight: '21px'
                    }}>
                      {selectedTab === 'list' && 'stampCount' in student ? `${student.stampCount}개` : '-'}
                    </div>
                    {selectedTab === 'list' && (
                      <button
                        style={{
                          width: '20px',
                          height: '20px',
                          padding: '2px',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <EditIcon width={16} height={16} color="#989898" />
                      </button>
                    )}
                  </div>
                  <div style={{
                    height: '45px',
                    padding: '12px 8px',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '9px'
                  }}>
                    <button
                      style={{
                        width: '20px',
                        height: '20px',
                        padding: '2px',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <SettingIcon width={16} height={16} color="#989898" />
                    </button>
                    <button
                      onClick={() => handleDeleteStudent(student.id)}
                      style={{
                        width: '20px',
                        height: '20px',
                        padding: '2px',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <TrashIcon width={16} height={16} color="#989898" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <div style={{
              paddingTop: '20px',
              paddingBottom: '20px',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '8px'
            }}>
              {/* 첫 페이지로 */}
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                style={{
                  width: '24px',
                  height: '24px',
                  padding: 0,
                  background: 'transparent',
                  border: 'none',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <LeftArrowDoubleIcon width={24} height={24} color={currentPage === 1 ? "#989898" : "#161616"} />
              </button>
              {/* 이전 페이지 */}
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                style={{
                  width: '24px',
                  height: '24px',
                  padding: 0,
                  background: 'transparent',
                  border: 'none',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <LeftArrowIcon width={24} height={24} color={currentPage === 1 ? "#989898" : "#161616"} />
              </button>
              {/* 페이지 번호 */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 0
              }}>
                {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage <= 2) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 1) {
                    pageNum = totalPages - 2 + i;
                  } else {
                    pageNum = currentPage - 1 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '8px',
                        background: currentPage === pageNum ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: currentPage === pageNum ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-assistive, #989898)',
                        fontSize: '14px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '14px'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              {/* 다음 페이지 */}
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                style={{
                  width: '24px',
                  height: '24px',
                  padding: 0,
                  background: 'transparent',
                  border: 'none',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <RightArrowIcon width={24} height={24} color={currentPage === totalPages ? "#989898" : "#161616"} />
              </button>
              {/* 마지막 페이지로 */}
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                style={{
                  width: '24px',
                  height: '24px',
                  padding: 0,
                  background: 'transparent',
                  border: 'none',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <RightArrowDoubleIcon width={24} height={24} color={currentPage === totalPages ? "#989898" : "#161616"} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubjectClassStudent;

